// Phase 2: Secure Key Vault (IndexedDB-backed)
// Hardened: timeouts, onblocked/onversionchange handling, no silent
// fire-and-forget transactions.

const DB_NAME = 'whispai-keyvault';
const STORE_NAME = 'keys';
const OPEN_TIMEOUT_MS = 4000;
const TX_TIMEOUT_MS = 4000;

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (fn: () => void) => { if (!settled) { settled = true; fn(); } };
    const timer = setTimeout(() => {
      console.warn('[secureKeyVault] open timed out');
      finish(() => reject(new Error('secureKeyVault: open timeout')));
    }, OPEN_TIMEOUT_MS);

    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(DB_NAME, 1);
    } catch (e) {
      clearTimeout(timer);
      finish(() => reject(e));
      return;
    }

    request.onupgradeneeded = () => {
      try {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      } catch (e) {
        console.warn('[secureKeyVault] upgrade failed', e);
      }
    };
    request.onblocked = () => {
      console.warn('[secureKeyVault] open blocked by another tab');
      clearTimeout(timer);
      finish(() => reject(new Error('secureKeyVault: open blocked')));
    };
    request.onsuccess = () => {
      clearTimeout(timer);
      const db = request.result;
      db.onversionchange = () => { try { db.close(); } catch { /* ignore */ } };
      finish(() => resolve(db));
    };
    request.onerror = () => {
      clearTimeout(timer);
      finish(() => reject(request.error ?? new Error('secureKeyVault: open error')));
    };
  });
}

function awaitTx(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (fn: () => void) => { if (!settled) { settled = true; fn(); } };
    const timer = setTimeout(() => {
      console.warn('[secureKeyVault] tx timed out');
      try { tx.abort(); } catch { /* ignore */ }
      finish(() => reject(new Error('secureKeyVault: tx timeout')));
    }, TX_TIMEOUT_MS);
    tx.oncomplete = () => { clearTimeout(timer); finish(() => resolve()); };
    tx.onabort = () => { clearTimeout(timer); finish(() => reject(tx.error ?? new Error('tx abort'))); };
    tx.onerror = () => { clearTimeout(timer); finish(() => reject(tx.error ?? new Error('tx error'))); };
  });
}

export async function savePrivateKey(userId: string, key: CryptoKey) {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put(key, `private:${userId}`);
    await awaitTx(tx);
  } catch (e) {
    console.warn('[secureKeyVault] savePrivateKey failed', e);
  }
}

export async function getPrivateKey(userId: string): Promise<CryptoKey | null> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    return await new Promise<CryptoKey | null>((resolve) => {
      const req = store.get(`private:${userId}`);
      req.onsuccess = () => resolve((req.result as CryptoKey) ?? null);
      req.onerror = () => resolve(null);
    });
  } catch (e) {
    console.warn('[secureKeyVault] getPrivateKey failed', e);
    return null;
  }
}

export async function clearPrivateKey(userId: string) {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(`private:${userId}`);
    await awaitTx(tx);
  } catch (e) {
    console.warn('[secureKeyVault] clearPrivateKey failed', e);
  }
}